import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CardCollection from '../components/CardCollection'
import Menu from '../components/Menu/Menu'
// import Searchbar from '../components/Searchbar';
// import useModelsStore from "../stores/models"
import styles from './Catalog.module.scss'
import SegmentedButtons from '../components/SegmentedButtons'
import usePrefStore from '../stores/prefs'
import useCollections from '../Hooks/useCollections'
// import { useLogger } from '../Contexts/Logger.jsx';
import CatalogSearchBar from '../components/Catalog/CatalogSearchBar'
import RetailCatalogSearchBar from '../components/Catalog/RetailCatalogSearchBar'
import { CatalogFilterContext } from '../Contexts/CatalogFilterContext'
import { RetailCatalogFilterContext } from '../Contexts/RetailCatalogFilterContext'
import glob_const from '../libs/glob_const'
import { resolveCatalogSearchTarget } from '../libs/catalogAccess'
import useRetailCollections from '../Hooks/useRetailCollections'
import useSales from '../Hooks/useSales'
import useRetailCatalogScope from '../Hooks/useRetailCatalogScope'
import { faGem, faBox } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styles_Searchbar from '../components/Searchbar.module.scss'

const tag_retail = 'RETAIL'

function Catalog() {
   const navigate = useNavigate()
   // const catalogMainAlign = usePrefStore((state) => state.catalogMainAlign)
   const { catalogMainAlign, retailRole, curCustomer, isRetailManager, serverRetailRole, prefsLoaded } = usePrefStore()
   const { getGammesAsSortedArray, getCollectionsInGammesAsSortedArray, getAllModelsAsArray } = useCollections()
   const { getRetailFamillesAsSortedArray, getAllRetailModelsAsArray, isRetailModelVisibleForCustomer } = useRetailCollections()
   const { getCustomerById } = useSales()
   const { customerType: effectiveCustomerType, customerForGrid } = useRetailCatalogScope()
   const [allGammes, setAllGammes] = useState<{ id: string; label: string }[]>([])
   const [gammeSegItems, setGammeSegItems] = useState<React.ReactElement | null>(null)
   const [gammeCollectionItems, setGammeCollectionItems] = useState<React.ReactElement[] | null>(null)
   // const { log, warn, debug, error } = useLogger()
   const { setSearchText } = { ...CatalogFilterContext() } as any // Texte de recherche
   const { setSearchText: setRetailSearchText } = { ...RetailCatalogFilterContext() } as any // Texte de recherche

   // effectiveCustomerType + customerForGrid : cf. Hooks/useRetailCatalogScope.

   const hasExcluBoutique = useMemo(() => {
      const customer = curCustomer ? getCustomerById(curCustomer) : null
      return customer?.exclu_boutique != null
   }, [curCustomer, getCustomerById])

   // Set des IDs de familles ayant au moins un modèle visible pour le client courant
   const visibleRetailFamilles = useMemo(() => {
      const set = new Set<string>()
      for (const model of getAllRetailModelsAsArray()) {
         if (isRetailModelVisibleForCustomer(model, effectiveCustomerType, hasExcluBoutique, customerForGrid)) {
            const fam = (model as unknown as Record<string, unknown>).fam as string | null
            if (fam) set.add(fam)
         }
      }
      return set
   }, [getAllRetailModelsAsArray, isRetailModelVisibleForCustomer, effectiveCustomerType, hasExcluBoutique, customerForGrid])

   useEffect(() => {
      setSearchText('')
      setRetailSearchText('')
   }, [setRetailSearchText, setSearchText])

   // Catalogue bijoux visible selon DocRetail/REUNION_5.md (lignes 177-186) :
   //   - Admin retail (global) → 🟢
   //   - Client (user-client) → 🛑
   //   - Manager N1 / N2 (interne) → 🛑
   //   - Interne sans rôle manager → 🟢
   const showJewelryCatalog = useMemo(() => {
      // Admin = trump (retail_admin global)
      if (retailRole === 'admin') return true
      // Tant que les prefs serveur n'ont pas été lues, on est conservateur : on cache les bijoux
      // pour éviter le flash "tout visible" au 1er login (race mount Catalog / setServerRetailRole).
      if (!prefsLoaded) return false
      if (serverRetailRole === 'client') return false
      if (isRetailManager) return false
      // Bug #5 : seul un INTERNE explicite (ou admin, traité plus haut) voit les bijoux.
      // Un rôle null/inconnu (user mal configuré) → pas de bijoux (fail-closed).
      return serverRetailRole === 'internal'
   }, [retailRole, serverRetailRole, isRetailManager, prefsLoaded])

   // Map gamme_id → Set des collec_id réellement utilisés par au moins un modèle.
   // Sert à filtrer les gammes/collections "fantômes" du référentiel (ex. gamme "ADE - A definir")
   // qui sont déclarées dans M3/AW mais n'ont aucun modèle associé.
   const usedCollectionsByGamme = useMemo(() => {
      const map = new Map<string, Set<string>>()
      for (const model of getAllModelsAsArray()) {
         const g = (model as unknown as Record<string, unknown>).gamme as string | undefined
         const c = (model as unknown as Record<string, unknown>).collec as string | undefined
         if (!g || !c) continue
         let set = map.get(g)
         if (!set) { set = new Set<string>(); map.set(g, set) }
         set.add(c)
      }
      return map
   }, [getAllModelsAsArray])

   useEffect(() => {
      if (!showJewelryCatalog) {
         setAllGammes([])
         return
      }
      // On ne garde que les gammes qui ont au moins un modèle.
      setAllGammes(getGammesAsSortedArray().filter(g => (usedCollectionsByGamme.get(g.id)?.size ?? 0) > 0))
   }, [getGammesAsSortedArray, showJewelryCatalog, usedCollectionsByGamme])

   const hasJewelry = showJewelryCatalog && allGammes.length > 0
   const hasRetail = glob_const.retail_enabled && visibleRetailFamilles.size > 0
   const [searchScope, setSearchScope] = useState<'jewelry' | 'retail'>('jewelry')

   // Aligne le scope par défaut quand l'un des deux n'est pas dispo
   useEffect(() => {
      if (!hasJewelry && hasRetail) setSearchScope('retail')
      else if (hasJewelry && !hasRetail) setSearchScope('jewelry')
   }, [hasJewelry, hasRetail])

   const scopeSelector = (hasJewelry && hasRetail) ? (
      <>
         <button className={searchScope === 'jewelry' ? styles_Searchbar.red : ''} onClick={() => setSearchScope('jewelry')} title="Bijoux">
            <FontAwesomeIcon icon={faGem} />
         </button>
         <button className={searchScope === 'retail' ? styles_Searchbar.red : ''} onClick={() => setSearchScope('retail')} title="Retail">
            <FontAwesomeIcon icon={faBox} />
         </button>
      </>
   ) : null

   const goDetail = useCallback(
      (gamme_id, coll_id) => {
         // log('goDetail', name)
         navigate(`/catalog-detail?gamme=${gamme_id}&collec=${coll_id}`, { replace: true })
      },
      [navigate]
   )

   const goRetailDetail = useCallback(
      (fam_id) => {
         navigate(`/retail-catalog-detail?fam=${fam_id}`, { replace: true })
      },
      [navigate]
   )

   function goSearchTarget() {
      const target = resolveCatalogSearchTarget(searchScope, hasJewelry, hasRetail)
      if (target) navigate(target, { replace: true })
   }

   function onSearch() {
      goSearchTarget()
   }

   function onFilter() {
      goSearchTarget()
   }

   function onQuickJumpChange(val) {
      // TODO
      // log(`onQuickJumpChange(${val})`)
      // log(`navigate(#gamme-${val}`)
      // navigate(`#gamme-${val}`)
      // navigate("/catalog/#gamme-JOI")
      // navigate("/toto")
      // ref.current.scrollIntoView()
      const element = document.getElementById(`gamme-${val}`)
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
   }

   useEffect(() => {
      const segments = allGammes.map((gamme) => {
         return { value: gamme.id, label: gamme.label }
      })

      if (glob_const.retail_enabled && visibleRetailFamilles.size > 0) {
         segments.push({
            value: tag_retail,
            label: 'Retail'
         })
      }

      setGammeSegItems(<SegmentedButtons className={styles.quickjump} round shadow opaque segments={segments} noselection medium medium_padding name="quickjump" onChange={onQuickJumpChange} />)
   }, [allGammes, retailRole, visibleRetailFamilles])

   useEffect(() => {
      const sections = allGammes.map((gamme) => {
         const usedCols = usedCollectionsByGamme.get(gamme.id)
         return (
            <Fragment key={`frag-${gamme.id}`}>
               <h1 id={`gamme-${gamme.id}`}>{gamme.label}</h1>
               <div className={styles.innerbox}>
                  {getCollectionsInGammesAsSortedArray(gamme.id, 'label')
                     .filter((collection) => usedCols?.has(collection.id))
                     .map((collection) => (
                        <CardCollection
                           key={`card-${gamme.id}-${collection.id}`}
                           label={collection.label}
                           image={`../catalogue/collections/${collection.id}.webp`}
                           onClick={() => {
                              goDetail(gamme.id, collection.id)
                           }}
                        ></CardCollection>
                     ))}
               </div>
            </Fragment>
         )
      })

      if (glob_const.retail_enabled) {
         const retailFamilles = getRetailFamillesAsSortedArray().filter(f => visibleRetailFamilles.has(f.id))
         if (retailFamilles.length > 0) {
            sections.push(
               <Fragment key={`frag-${tag_retail}`}>
                  <h1 id={`gamme-${tag_retail}`}>Retail</h1>
                  <div className={styles.innerbox}>
                     {retailFamilles.map((famille) => (
                        <CardCollection
                           key={`card-${tag_retail}-${famille.id}`}
                           label={famille.label}
                           image={`../catalogue/collections/retail-${famille.id.toLowerCase()}.webp`}
                           onClick={() => {
                              goRetailDetail(famille.id)
                           }}
                        />
                     ))}
                  </div>
               </Fragment>
            )
         }
      }

      setGammeCollectionItems(sections)
   }, [allGammes, retailRole, getCollectionsInGammesAsSortedArray, getRetailFamillesAsSortedArray, goDetail, goRetailDetail, visibleRetailFamilles, usedCollectionsByGamme])

   // useEffect(() => {
   //    log('getGammeKeys', getGammeKeys())
   //    log('getCollectionKeysForGamme: jewelry', getCollectionKeysForGamme("jewelry"))
   // }, [getGammeKeys, getCollectionKeysForGamme])

   return (
      <Fragment>
         <Menu page="catalog" />
         <div className={`${styles.catalog} ${catalogMainAlign === 'center' ? styles.catacenter : ''}`}>
            {searchScope === 'retail' && hasRetail
               ? <RetailCatalogSearchBar onSearch={onSearch} onFilter={onFilter} extraTools={scopeSelector} />
               : (hasJewelry
                  ? <CatalogSearchBar onSearch={onSearch} onFilter={onFilter} extraTools={scopeSelector} />
                  : (hasRetail ? <RetailCatalogSearchBar onSearch={onSearch} onFilter={onFilter} extraTools={scopeSelector} /> : null))
            }
            {gammeSegItems}
            {gammeCollectionItems}
         </div>
      </Fragment>
   )
}

export default Catalog
